import React, { useEffect, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { Modal, Box, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useDispatch } from 'react-redux';
import { setDayEndPopup } from '../../stateManagement/actions/dayEndActions';
import { useSelector } from 'react-redux';
import { Loader } from '../loader/Loader';
import "./DayEndPopup.css";
import { useNavigate } from 'react-router-dom';
import { completeDayEnd, getDayEndStatusFromSession, updateDayEndStatusInSession, updateLasteEndDate } from './DayEndUtils';
import dayjs from 'dayjs';
import { GenericModal, getGenericModalProps, initialGenericModalProps } from '../../features/viewOrder/ViewOrderUtils';
import CustomGenericModal from './CustomGenericModal';
import { StatusType } from './DayEndBox';
import RenderSvg from '../renderSvg/RenderSvg';
import { sendSlackAlert, SlackChannel } from '../../services/slackService';
import { safeStringify } from './DmsUtils';

const DayEndModal: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { dayEndPopupState } = useSelector((state: any) => state.dayEnd);

  const handleClose = () => {
    dispatch(setDayEndPopup(false));
  }

  const initialOrderCount = {
    pending: 0,
    invoiced: 0,
    outForDelivery: 0,
    startDate: '',
    pendingDays: 0,
    status: ["PENDING, INVOICED, OUT FOR DELIVERY"]
  }

  const [orderCount, setOrderCount] = useState(initialOrderCount);
  const [loader, setLoader] = useState(true);
  const [status, setStatus] = useState<any>({});
  const [completed, setCompleted] = useState(false)
  const [message, setMessage] = useState("");
  const [genericModalProps, setGenericModalProps] =
    useState<GenericModal>(initialGenericModalProps);

  const getData = async () => {
    if (!dayEndPopupState) {
      return;
    }

    try {
      setLoader(true);

      await updateDayEndStatusInSession();
      const dayEndObj = getDayEndStatusFromSession();

      setOrderCount({
        pending: dayEndObj.pendingCount,
        invoiced: dayEndObj.invoicedCount,
        outForDelivery: dayEndObj.outForDeliveryCount,
        startDate: dayEndObj.startDate.split(' ')[0],
        pendingDays: dayjs(dayEndObj.endDate).diff(dayjs(dayEndObj.startDate), "day") + 1,
        status: dayEndObj.mandatoryStatus
      });
      
      const m: string[] = []

      dayEndObj.mandatoryStatus.forEach((status: string) => {

        if (status === "PENDING") {
          m.push("Invoice");
        }
        if (status === "INVOICED") {
          m.push("Loadout");
        }
        if (status === "OUT FOR DELIVERY") {
          m.push("Settlement");
        }
      });

      setMessage(m.join(" -> "));

      const task1Status = getProgressStatus(dayEndObj.status.invoice)
      const task2Status = getProgressStatus(dayEndObj.status.loadout)
      const task3Status = getProgressStatus(dayEndObj.status.delivery)
      setStatus({
        task1Status,
        task2Status,
        task3Status,
      });

      if (dayEndObj.canMarkDayEnd) {
        setCompleted(true)
      } else {
        setCompleted(false)
      }     
    } catch (error) {
      console.error("Error fetching order data for day end:", error);
    }
    finally {
      setLoader(false);
    }
  }

  useEffect(() => {
    getData();
  } , [dayEndPopupState]);


  if (!dayEndPopupState) {
    return null;
  }

  const getProgressStatus = (statusType: string) => {
    if (statusType === StatusType.NotStarted) {
      return {
        label: "Start Task",
        className: "start-button",
        svgIcon: "NotStartedSVG"
      };
    } else if (statusType === StatusType.InProgress) {
      return {
        label: "In Progress",
        className: "in-progress-button",
        svgIcon: "InProgressSVG"
      }
    }
    return {
      label: "Completed",
      className: "completed-button",
      svgIcon: "CompletedSVG"
    };
  };

  const handleClickTask = (taskNumber: number) => {
    // Logic to navigate to respective module based on task number
    if (taskNumber === 1) {
      if (status.task1Status.label === "Completed") {
        return;
      }
      navigate('/dashboard/sale/manageOrders');
      dispatch(setDayEndPopup(false));
    } else if (taskNumber === 2) {
      if (status.task2Status.label === "Completed") {
        return;
      }
      navigate('/dashboard/delivery/loadouts/preseller');
      dispatch(setDayEndPopup(false));
    } else if (taskNumber === 3) {
      if (status.task3Status.label === "Completed") {
        return;
      }
      navigate('/dashboard/manageLoadout');
      dispatch(setDayEndPopup(false));
    }
  };


  const resetGenericModalProps = () => setGenericModalProps(initialGenericModalProps);

  const markDayEnd = async () => {
    await updateDayEndStatusInSession()
    const newDayEndObj = getDayEndStatusFromSession();
    if (newDayEndObj && !newDayEndObj.canMarkDayEnd) {
      setGenericModalProps(getGenericModalProps(
        "positive",
        "Please complete all the activites"
      ))
      return;
    }

    //update last day end in table
    try{

      await updateLasteEndDate()
      setGenericModalProps(getGenericModalProps(
        "success",
        "Day End marked successfully",
        "Continue",
        "",
        () => {
          resetGenericModalProps();
          navigate("/dashboard/sale/manageOrders");
        },
      ))
      await completeDayEnd();
    } catch (error) {
      setGenericModalProps(getGenericModalProps(
        "negative",
        "Failed to mark Day End. Please try again."
      ))
      await sendSlackAlert(
        SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
        "Errow while marking dayend",
        `Error: ${(error as Error).message}`,
        safeStringify({ error })
      );
      console.error(error);
    }
  }
  return (
    <>
      
      <Modal
        open={dayEndPopupState}
        onClose={handleClose}
        aria-labelledby="day-end-modal-title"
        aria-describedby="day-end-modal-description"
      >
        <Box sx={styles.modalBox}>
          {
            loader ? 
            <div style={{display: "flex", height: "100%", alignItems: "center", justifyContent: "center"}}>
              <Loader/>
            </div>
            :
            <div style={styles.container}>
              {/* Header */}
              <div style={styles.header}>
                <div style={styles.headerLeft}>
                  <div style={{...styles.iconCircle, background: completed ? "#E7FFE7" : "#fff1f1" }} >
                    {completed ?
                      <CheckIcon sx={{ color: '#16a34a', fontSize: 28 }} />
                      :
                      <ErrorOutlineIcon sx={{ color: '#ef4444', fontSize: 28 }} />
                    }
                  </div>
                  <h2 style={styles.title}>{`${completed ? "You have completed Day end Settlement" : "Complete your Day End Settlement"}`}</h2>
                </div>
                <IconButton onClick={handleClose} style={styles.closeButton}>
                  <CloseIcon />
                </IconButton>
              </div>

              {/* Content */}
              <div style={styles.content}>
                <p style={styles.description}>
                  {`${completed ? `Congratulations you have successfully completed all Day End task` : `You have ${orderCount.pendingDays} days of pending task. Please complete all activities to open different modules.`}`}
                 
                </p>

                <div style={{display: 'flex', justifyContent: "space-between", alignItems: 'center', marginBottom: "12px"}}>
                  <div style={styles.dateInfo}>
                    <div style={{backgroundColor: "#FFF1FE", padding: "4px 8px", borderRadius: "20px"}}>
                      <span style={{...styles.label, color: "#7B087B"}}>Last Settlement Date : </span>
                      <span style={styles.dateValue}>{orderCount.startDate}</span>
                    </div>
                    <div style={{backgroundColor: "#FFF1F1", padding: "4px 8px", borderRadius: "20px"}}>
                      <span style={{...styles.label, color: "#B54747"}}>Pending Days : </span>
                      <span style={styles.pendingDays}>{orderCount.pendingDays} days</span>
                    </div>
                  </div>
                </div>

                <h3 style={styles.actionTitle}>Action Required</h3>

                {/* Task List */}
                <div style={styles.taskList}>
                  {/* Task 1 */}
                  {
                    orderCount.status.includes("PENDING") &&
                    <div style={styles.taskItem}>
                      <span style={styles.taskNumber}>
                        {[ orderCount.status.includes("PENDING") ? 1 : null,
                           orderCount.status.includes("INVOICED") || orderCount.status.includes("PARTIALLY INVOICED") ? 2 : null,
                           orderCount.status.includes("OUT FOR DELIVERY") ? 3 : null
                        ].filter(Boolean).indexOf(1) + 1}
                      </span>
                      <div style={styles.taskContent}>
                        <div style={styles.taskHeader}>
                          <h4 style={styles.taskTitle}>{`Pending Orders (${orderCount.pending})`}</h4><RenderSvg name={status.task1Status.svgIcon} />
                        </div>
                        <p style={styles.taskDescription}>Review and complete pending invoices</p>
                      </div>
                      <button onClick={() => handleClickTask(1)} style={styles.startButton} className={status.task1Status.className} >{status.task1Status.label}</button>
                    </div>

                  }
                  {/* Task 2 */}
                  {
                    (orderCount.status.includes("INVOICED") || orderCount.status.includes("PARTIALLY INVOICED")) &&
                    <div style={styles.taskItem}>
                      <span style={styles.taskNumber}>
                        {[ orderCount.status.includes("PENDING") ? 1 : null,
                           orderCount.status.includes("INVOICED") || orderCount.status.includes("PARTIALLY INVOICED") ? 2 : null,
                           orderCount.status.includes("OUT FOR DELIVERY") ? 3 : null
                        ].filter(Boolean).indexOf(2) + 1}
                      </span>
                      <div style={styles.taskContent}>
                        <div style={styles.taskHeader}>
                          <h4 style={styles.taskTitle}>{`Loadout Creation (${orderCount.invoiced})`}</h4><RenderSvg name={status.task2Status.svgIcon} />
                        </div>
                        <p style={styles.taskDescription}>Create and verify loadout records</p>
                      </div>
                      <button onClick={() => handleClickTask(2)} style={styles.startButton} className={status.task2Status.className}>{status.task2Status.label}</button>
                    </div>
                  }
                  {/* Task 3 */}
                  {
                    orderCount.status.includes("OUT FOR DELIVERY") &&
                    <div style={styles.taskItem}>
                      <span style={styles.taskNumber}>
                        {[ orderCount.status.includes("PENDING") ? 1 : null,
                           orderCount.status.includes("INVOICED") || orderCount.status.includes("PARTIALLY INVOICED") ? 2 : null,
                           orderCount.status.includes("OUT FOR DELIVERY") ? 3 : null
                        ].filter(Boolean).indexOf(3) + 1}
                      </span>
                      <div style={styles.taskContent}>
                        <div style={styles.taskHeader}>
                          <h4 style={styles.taskTitle}>{`Delivery Confirmation (${orderCount.outForDelivery})`}</h4><RenderSvg name={status.task3Status.svgIcon} />
                        </div>
                        <p style={styles.taskDescription}>Settle and reconcile all invoices</p>
                      </div>
                      <button onClick={() => handleClickTask(3)} style={styles.startButton} className={status.task3Status.className}>{status.task3Status.label}</button>
                    </div>
                  }
                </div>

                {/* Recommendation */}
                <div style={styles.recommendation}>
                  <ErrorOutlineIcon sx={{ color: '#f97316', fontSize: 20, marginTop: '2px', flexShrink: 0 }} />
                  <p style={styles.recommendationText}>
                    <span style={styles.recommendationBold}>Recommendation : </span>
                    You should proceed step wise as given ({message}) in the pop up for the easy process
                  </p>
                </div>
              </div>
            </div>
          }
        </Box>
      </Modal>
    </>
  );
};

const styles = {
  openButton: {
    padding: '12px 24px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  modalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '500px',
    height: "100%",
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: 24,
    outline: 'none',
  },
  container: {
    backgroundColor: 'white',
    borderRadius: '8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px',
    borderBottom: '1px solid #e5e7eb',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  iconCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  closeButton: {
    color: '#9ca3af',
  },
  content: {
    padding: '12px',
  },
  description: {
    color: '#6b7280',
    marginBottom: '12px',
    fontSize: '13px',
    lineHeight: '1.5',
  },
  dateInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '12px',
  },
  label: {
    color: '#6b7280',
  },
  dateValue: {
    fontWeight: '500',
    color: '#111827',
  },
  pendingDays: {
    fontWeight: '500',
  },
  actionTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '16px',
    marginTop: 0,
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  taskItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    transition: 'border-color 0.2s',
  },
  taskNumber: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#4b5563',
    minWidth: '24px',
    backgroundColor: '#f5f5f5',
    borderRadius: "50%",
    textAlign: 'center' as const,
  },
  taskContent: {
    flex: 1,
  },
  taskHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  taskTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  taskDescription: {
    fontSize: '12px',
    color: '#6b7280',
    margin: 0,
  },
  startButton: {
    padding: '8px 24px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap',
  },
  recommendation: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: '8px',
    display: 'flex',
    gap: '8px',
  },
  recommendationText: {
    fontSize: '12px',
    color: '#9a3412',
    margin: 0,
    lineHeight: '1.5',
  },
  recommendationBold: {
    fontWeight: '600',
  },

};

export default DayEndModal;
